// All Firestore IO for the Tuesday Call Sheet. One doc per week in the
// `callSheets` collection, doc id = the week's Tuesday (YYYY-MM-DD, local).
// Owner-only feature: firestore.rules restricts callSheets to isOwner().
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { getContactsByIds } from "./firebasefunctions";
import { dealContactIds } from "./contactCrm";
import { dueState } from "./followUpPlan";
import { updateTouch } from "./followUpPlansStore";
import {
  buildCallSheetEntries,
  weekKeyFor,
  prevWeekKeyFor,
  localIsoDate,
} from "./callSheet";

const COL = "callSheets";

const mmddyyyy = (iso) => {
  const [y, m, d] = String(iso).split("-");
  return `${m}/${d}/${y}`;
};

const initialsOf = (user) =>
  ((user?.firstname?.[0] || "") + (user?.lastname?.[0] || "")).toUpperCase();

// Builds this week's sheet if it doesn't exist yet. agentId scopes the sheet to
// one person's own clients (see buildCallSheetEntries).
//
// Sheets are built once a week, so a sheet already sitting in Firestore under
// the old, unscoped rules would otherwise stay wrong until next Tuesday. When
// the stored scope doesn't match the one being asked for — including the
// migration case where the field predates agentId entirely — the entries are
// rebuilt from today's deals and the doc is overwritten. Per-entry progress
// (calls made, notes written) carries over for every deal that survives the
// rebuild, and createdAt/popupShownAt are kept so a rebuild never re-fires the
// weekly auto-popup.
export async function ensureCurrentSheet(deals, ownerEmail, now = new Date(), agentId) {
  const weekOf = weekKeyFor(now);
  const ref = doc(firestored, COL, weekOf);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() : null;
  const scope = agentId || "";
  if (existing && (existing.agentId || "") === scope) {
    return { sheet: { ...existing, weekOf }, created: false };
  }

  const prevSnap = await getDoc(doc(firestored, COL, prevWeekKeyFor(now)));
  const prevEntries = prevSnap.exists() ? prevSnap.data().entries || [] : [];

  const ids = [...new Set((deals || []).flatMap((d) => dealContactIds(d)))];
  const contacts = ids.length ? await getContactsByIds(ids) : [];
  const contactsById = {};
  (contacts || []).filter(Boolean).forEach((c) => {
    const key = c.firebaseId || c.id;
    if (key) contactsById[key] = c;
  });

  let entries = buildCallSheetEntries(deals, prevEntries, contactsById, now, agentId);

  if (existing) {
    const priorById = new Map(
      (existing.entries || []).map((e) => [String(e.dealId), e]),
    );
    entries = entries.map((e) => {
      const prior = priorById.get(String(e.dealId));
      if (!prior) return e;
      return {
        ...e,
        calledAt: prior.calledAt ?? null,
        note: prior.note || "",
        noteLine: prior.noteLine || "",
        noteUpdatedAt: prior.noteUpdatedAt ?? null,
      };
    });
  }

  const sheet = {
    weekOf,
    ownerEmail: ownerEmail || "",
    agentId: scope,
    createdAt: existing?.createdAt ?? Date.now(),
    popupShownAt: existing ? (existing.popupShownAt ?? null) : null,
    entries,
  };
  await setDoc(ref, sheet);
  return existing ? { sheet, created: false, rebuilt: true } : { sheet, created: true };
}

// Caller contract: weekOf is fixed at subscribe time — a caller that stays
// mounted across a Monday-to-Tuesday rollover must detect the week change
// (weekKeyFor(new Date()) differs from the subscribed weekOf) and resubscribe.
export function subscribeCurrentSheet(cb, now = new Date()) {
  const weekOf = weekKeyFor(now);
  return onSnapshot(doc(firestored, COL, weekOf), (snap) => {
    cb(snap.exists() ? { ...snap.data(), weekOf } : null);
  });
}

export async function saveSheetEntries(weekOf, entries) {
  await updateDoc(doc(firestored, COL, weekOf), { entries });
}

export async function stampPopupShown(weekOf) {
  await updateDoc(doc(firestored, COL, weekOf), { popupShownAt: Date.now() });
}

// Saves/edits the note on a sheet entry AND mirrors it into the deal's single
// `notes` sticky-note string as a signed, dated line:
//   "Talked pricing. -HE 08/26/2026"
// (plain hyphen + the user's initials; no initials on the user = text then
// date, no signature). Editing replaces the exact line this entry wrote
// earlier (tracked in entry.noteLine) so edits never stack.
// Known limitation: the deal's sticky note is a single string edited elsewhere
// by a debounced-autosave editor, so a simultaneous sticky-note edit on the
// deal page is last-write-wins (single-user feature, weekly cadence —
// accepted; runTransaction is the later hardening if it ever bites).
export async function saveCallNote(sheet, dealId, text, user, now = new Date()) {
  const entries = (sheet.entries || []).map((e) => ({ ...e }));
  const entry = entries.find((e) => String(e.dealId) === String(dealId));
  if (!entry) throw new Error("Client is not on this sheet");

  const trimmed = String(text || "").trim();
  const dealRef = doc(firestored, "deals", String(dealId));
  const dealSnap = await getDoc(dealRef);
  const existing = dealSnap.exists() ? dealSnap.data().notes || "" : "";
  const initials = initialsOf(user);
  const newLine = `${trimmed}${initials ? ` -${initials}` : ""} ${mmddyyyy(localIsoDate(now))}`;

  // Function replacer so note text is inserted verbatim — a plain string
  // replacement would process $-patterns ($$, $&, $', $`) in the new line.
  const nextNotes =
    entry.noteLine && existing.includes(entry.noteLine)
      ? existing.replace(entry.noteLine, () => newLine)
      : existing
        ? `${existing}\n\n${newLine}`
        : newLine;

  await setDoc(dealRef, { notes: nextNotes }, { merge: true });

  entry.note = trimmed;
  entry.noteLine = newLine;
  entry.noteUpdatedAt = Date.now();
  await saveSheetEntries(sheet.weekOf, entries);
  return entries;
}

// Recently-closed check-off: also complete the earliest DUE follow-up touch so
// Past Client Follow-Ups never nags about the same call. No due touch = no-op.
export async function completeDueTouchForDeal(dealId, userId, todayIso) {
  const snap = await getDoc(doc(firestored, "followUpPlans", String(dealId)));
  if (!snap.exists()) return false;
  const plan = snap.data();
  if (plan.status !== "active") return false;
  const touches = plan.touches || {};
  const dueKeys = Object.keys(touches).filter(
    (k) => dueState(touches[k], todayIso).bucket === "due",
  );
  if (!dueKeys.length) return false;
  dueKeys.sort((a, b) => String(touches[a].due).localeCompare(String(touches[b].due)));
  await updateTouch(String(dealId), dueKeys[0], {
    state: "done",
    doneAt: Date.now(),
    doneBy: userId || "",
    method: "call",
  });
  return true;
}
